import type {
  ActivityTrackingConfiguration,
  SelfDescribingJson,
  TrackerConfiguration,
} from '@snowplow/browser-tracker';
import {
  enableActivityTracking,
  newSession,
  newTracker,
  setUserId as setUserIdBrowser,
  trackSelfDescribingEvent as trackBrowser,
} from '@snowplow/browser-tracker';
import { trackSelfDescribingEvent as trackWebview } from '@snowplow/webview-tracker';
import { createPromiseStore } from './utils/batch';
import { createObserver } from './utils/observer';

interface InitializeOptions {
  trackerConfiguration: TrackerConfiguration;
  collectorUrl: string;
  activityTrackingConfiguration: ActivityTrackingConfiguration;
  trackerId: string;
  isWebview?: boolean;
}

export interface SnowplowClient {
  initialize: (options: InitializeOptions) => void;
  track: (evt: SelfDescribingJson) => void;
  setUserId: (userId: string) => void;
}

const SNOWPLOW_INITIALIZED_EVENT = 'snowplow-initialized';

export const createSnowplowClient = (): SnowplowClient => {
  const observer = createObserver();
  const { batch } = createPromiseStore('snowplow');
  let initialized = false;
  let isWebview = false;

  const initialize = batch((options: InitializeOptions): void => {
    if (initialized) {
      return;
    }

    isWebview = options.isWebview ?? false;

    newSession();
    newTracker(options.trackerId, options.collectorUrl, options.trackerConfiguration);
    enableActivityTracking(options.activityTrackingConfiguration);

    initialized = true;

    // NOTE: 초기화가 완료되면 대기 중이던 observer들을 실행합니다.
    observer.notify(SNOWPLOW_INITIALIZED_EVENT);
  });

  const track = (params: SelfDescribingJson): void => {
    const { schema, data } = params;

    const event: Readonly<SelfDescribingJson> = {
      schema,
      data: data ?? {},
    };

    const executeTracking = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Track] Tracked:', event);
      }
      if (isWebview) {
        trackWebview({
          event,
        });
      } else {
        trackBrowser({
          event,
        });
      }
    };

    if (initialized) {
      executeTracking();
      return;
    }

    // NOTE: 초기화가 완료되기 전에 실행된 경우, 초기화된 후에 실행되도록 합니다.
    observer.observe(SNOWPLOW_INITIALIZED_EVENT, executeTracking);
  };

  const setUserId = (userId: string): void => {
    const executeSetUserId = () => {
      setUserIdBrowser(userId);
    };

    if (initialized) {
      executeSetUserId();
      return;
    }

    // NOTE: 초기화가 완료되기 전에 실행된 경우, 초기화된 후에 실행되도록 합니다.
    observer.observe(SNOWPLOW_INITIALIZED_EVENT, executeSetUserId, { priority: 'high' });
  };

  return {
    initialize,
    track,
    setUserId,
  };
};

export const { initialize, track, setUserId } = createSnowplowClient();
